import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormWrap } from '../FormContainer/FormContainer';
import Paragraph from '../Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { EVENT_TYPES_ENUM } from '../../util/enum/api/crmEvents';
import useApplicationUid from '../../hooks/useApplicationUid';

const VideoArea = styled.video`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  color: ${themeColors.colorBackgroundDropzone};
  border-radius: ${variables.borderRadius.borderRadiusMd};
  ::cue {
    color: ${themeColors.colorPrimaryLight};
  }
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.3);
`;

const ErrorContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-radius: ${variables.borderRadius.borderRadiusMd};
  height: 250px;
  width: 100%;
  background-color: ${themeColors.colorPrimaryLight};
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.07);
`;

const VideoSource = styled.source``;
const ClosedCaptions = styled.track``;

const VideoPlayer = ({
  formId,
  url,
  subtitleTrack,
  title,
  subtitle,
  textBelow,
  subtextBelow,
  handleCrmEvent = () => {},
  pagePath,
}) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const appUid = useApplicationUid();
  const videoRef = useRef();
  const [videoDuration, setVideoDuration] = useState(0);

  useEffect(() => {
    setHasError(!url);
  }, [url]);

  const onMetadataLoad = () => {
    if (!url) {
      return;
    }
    const pageVideo = videoRef?.current;

    if (!pageVideo) {
      return;
    }

    const { duration } = pageVideo;
    setVideoDuration(duration);
  };

  const onVideoPlay = () => {
    handleCrmEvent(appUid?.ApplicationUid, {
      Data: [
        {
          key: 'Source',
          value: pagePath,
        },
      ],
      EventType: EVENT_TYPES_ENUM.VideoPlay,
    });
  };

  const onVideoPause = () => {
    if (videoRef?.current?.currentTime === videoDuration) {
      return;
    }

    handleCrmEvent(appUid?.ApplicationUid, {
      Data: [
        {
          key: 'Source',
          value: pagePath,
        },
      ],
      EventType: EVENT_TYPES_ENUM.VideoPause,
    });
  };

  const onVideoEnd = () => {
    handleCrmEvent(appUid?.ApplicationUid, {
      Data: [
        {
          key: 'Source',
          value: pagePath,
        },
      ],
      EventType: EVENT_TYPES_ENUM.VideoEnd,
    });
  };

  return (
    <FormWrap id={formId}>
      {title && (
        <Paragraph weight={600} mb={4} color={themeColors.colorTextSecondary}>
          {title}
        </Paragraph>
      )}
      {subtitle && (
        <Paragraph color={themeColors.colorTextGrey} mb={4}>
          {subtitle}
        </Paragraph>
      )}
      {hasError ? (
        <ErrorContainer>
          <Paragraph color={themeColors.colorErrorMessage}>
            {t('videos.genericPlaybackError')}
          </Paragraph>
        </ErrorContainer>
      ) : (
        <VideoArea
          ref={videoRef}
          controls
          onPlay={onVideoPlay}
          onPause={onVideoPause}
          onEnded={onVideoEnd}
          onLoadedMetadata={onMetadataLoad}
        >
          <VideoSource src={url} type="video/mp4" />
          <ClosedCaptions
            default
            kind="captions"
            srcLang="EN"
            src={subtitleTrack}
            label="EN"
          />
        </VideoArea>
      )}
      {textBelow && (
        <Paragraph color={themeColors.colorTextSecondary} mt={4}>
          {textBelow}
        </Paragraph>
      )}
      {subtextBelow && (
        <Paragraph color={themeColors.colorTextGrey} mt={4}>
          {subtextBelow}
        </Paragraph>
      )}
    </FormWrap>
  );
};
VideoPlayer.propTypes = {
  formId: PropTypes.string,
  url: PropTypes.string,
  subtitleTrack: PropTypes.string,
  // altText: PropTypes.string, /* For future use */
  // posterUrl: PropTypes.string, /* For future use */
  title: PropTypes.string,
  subtitle: PropTypes.string,
  textBelow: PropTypes.string,
  subtextBelow: PropTypes.string,
  handleCrmEvent: PropTypes.func,
  pagePath: PropTypes.string,
};

export default VideoPlayer;
